// IMPORTANT: TODO: This needs to be tested witih all event types.

import React, { useState } from 'react';
import cc from 'classcat';
import {
  IconDefinition,
  faHistory,
  faInfoCircle,
  faHashtag,
  faPencil,
  faSave,
  faTimes
} from '@fortawesome/pro-solid-svg-icons';

import { Col, Row } from '../../../grid';
import Icon from '../../atoms/icon';
import Tag from '../../atoms/tag';
import Collapse from '../collapse';
import Tooltip from '../tooltip';
import Button from '../../molecules/button';
import Toast from '../toast';
import {
  IEvent,
  getEventTypes,
  EventTypes,
  useGetPatientEvents,
  EditEventNote,
  getInvalidationFieldDisplay,
  getInvalidationValueDisplay
} from '../../../libs/event';
import { IAccountBaseSettings } from '../../../libs/account';
import {
  FormatDateTime,
  FormatDate,
  FormatTime,
  TIME_FORMATS,
  WEEKDAYS_SHORT
} from '../../../libs/time';
import { AppKey } from '../../../libs/apps';
import { FormatContact } from '../../../libs/contact';
import { useMetaData } from '../../../contexts/metadata';
import { useApps } from '../../../contexts/apps';
import { useAuth } from '../../../contexts/auth';

interface PatientEventNoteProps {
  data: IEvent;
}

const PatientEventNote: React.FC<PatientEventNoteProps> = ({ data }) => {
  switch (data.type) {
    case EventTypes.Alert:
      return <AlertNote note={data} />;
    case EventTypes.AlertSnoozed:
      return <SnoozedAlertNote note={data} />;
    case EventTypes.AlertUnsnoozed:
      return <UnsnoozedAlertNote note={data} />;
    case EventTypes.AlertResolved:
      return <ResolvedAlertNote note={data} />;
    case EventTypes.AlertActionNote:
      return <Note note={data} />;
    case EventTypes.Note:
      return <Note note={data} />;
    case EventTypes.Prescription:
      return <PrescriptionNote note={data} />;
    case EventTypes.PrescriptionEdited:
      return <EditPrescriptionNote note={data} />;
    case EventTypes.PrescriptionCanceled:
      return <CancelPrescriptionNote note={data} />;
    case EventTypes.PatientEdited:
      return <EditNote note={data} />;
    case EventTypes.PatientResolved:
      return <ResolvePatientNote note={data} />;
    case EventTypes.PatientSnoozed:
      return <SnoozePatientNote note={data} />;
    case EventTypes.PatientUnsnoozed:
      return <UnsnoozedPatientNote note={data} />;
    case EventTypes.PatientSuspended:
      return <SuspendNote note={data} />;
    case EventTypes.PatientUnsuspended:
      return <SuspendNote note={data} />;
    case EventTypes.PatientArchived:
      return <ArchiveNote note={data} />;
    case EventTypes.PatientUnarchived:
      return <ArchiveNote note={data} />;
    case EventTypes.SessionInvalidated:
      return <InvalidateNote note={data} />;
    case EventTypes.EMROrder:
      return <NewOrderNote note={data} />;
    case EventTypes.PushMessage:
      return <PushMessageNote note={data} />;
    case EventTypes.TimeTracked:
      return <TimeTrackedNote note={data} />;
    default:
      return <GenericNote note={data} />;
  }
};

interface NoteIconProps {
  icon: IconDefinition;
  iconColor: string;
}
const NoteIcon: React.FC<NoteIconProps> = ({ icon, iconColor }) => {
  return (
    <Col lg={1} className="pa-4">
      <Icon className={iconColor} icon={icon} />
    </Col>
  );
};

interface NoteAlertIdProps {
  alertId: number;
}
const NoteAlertId: React.FC<NoteAlertIdProps> = ({ alertId }) => {
  return (
    <Col lg={1} className="pa-4">
      <Tooltip content={`Alert #${alertId}`}>
        <Icon icon={faHashtag} className="text-gray2" />
      </Tooltip>
    </Col>
  );
};

interface NoteMessageProps {
  app: AppKey;
  date: Date;
  message?: string;
}

const NoteMessage: React.FC<NoteMessageProps> = ({
  children,
  app,
  date,
  message
}) => {
  const { getAppName } = useApps();
  const appName = app ? getAppName(app) : null;
  return (
    <Col lg={22} className="pa-4">
      <strong className="mr-2">{FormatDate(date)}</strong>
      <span className="mr-2">{FormatTime(date, TIME_FORMATS.TIME_TZ)}</span>
      {appName && <Tag text={appName} className="mr-4" />}
      {message && <span>{message}</span>}
      {children}
    </Col>
  );
};

interface NoteNoteProps {
  note: string;
}

const NoteNote: React.FC<NoteNoteProps> = ({ note }) => {
  return (
    <div className="bg-light-grey rounded pa-4 mt-4 mb-2">
      <strong>Note:</strong> {note}
    </div>
  );
};

interface NoteOwnerProps {
  contactId?: number;
  userId?: number;
  note?: IEvent;
  onEdit?: () => void;
}
const NoteOwner: React.FC<NoteOwnerProps> = ({
  contactId,
  userId,
  note,
  onEdit
}) => {
  const { contacts, users, isReadOnly } = useMetaData();
  const { user } = useAuth(); // NOTE: Should this hook be used each time or should user be passed down as a prop?
  const contact = contacts && contactId ? contacts[contactId] : null;
  const owner = users && userId ? users[userId] : null;
  const ownerIsUser = user.id === userId;
  const canEdit =
    !isReadOnly &&
    note &&
    note.type === EventTypes.Note &&
    ownerIsUser &&
    onEdit;
  const ownerInfo = userId
    ? `By ${owner ? owner.email : ''}${ownerIsUser ? ' (you)' : ''}`
    : contactId
      ? `By ${contact ? FormatContact(contact) : ''} (#${contactId})`
      : null;

  const tooltipContent = canEdit ? 'Edit your note' : ownerInfo;

  const iconToShow = canEdit ? faPencil : faInfoCircle;

  return (
    <Col lg={1} className="pa-4">
      <div className="flex align-center justify-center">
        <Tooltip content={tooltipContent}>
          <Button
            bare
            variant="minimal"
            className="pa-0"
            onClick={canEdit ? onEdit : undefined}
            style={{ cursor: canEdit ? 'pointer' : 'default' }}
          >
            <Icon icon={iconToShow} className="text-gray2" />
          </Button>
        </Tooltip>
      </div>
    </Col>
  );
};

interface NoteFrameProps {
  className?: string;
}

const NoteFrame: React.FC<NoteFrameProps> = (props) => {
  const classes = cc([
    props.className,
    'border-1 border-solid border-light-grey rounded mb-4 bg-white'
  ]);
  return <Row className={classes}>{props.children}</Row>;
};

interface NoteProps {
  note: IEvent;
}

const GenericNote: React.FC<NoteProps> = ({ note }) => {
  const { terms } = useMetaData();
  const eventTypes = getEventTypes(terms);
  const config = eventTypes[note.type];
  return (
    <NoteFrame>
      <NoteIcon icon={config.icon} iconColor={config.iconColor} />
      <NoteMessage
        message={config.description}
        date={note.createdAt}
        app={note.app}
      />
      <NoteOwner contactId={note.contactId} userId={note.userId} />
    </NoteFrame>
  );
};

const ALERT_RELATED_EVENT_TYPES = [
  EventTypes.PatientSnoozed,
  EventTypes.PatientResolved,
  EventTypes.AlertResolved,
  EventTypes.AlertSnoozed,
  EventTypes.AlertActionNote
];

const GetRelatedAlertEvents = (note: IEvent, isEnabled: boolean) => {
  const meta = JSON.parse(note.meta);
  const alertId = meta.alertId;
  const related = [];
  const data = useGetPatientEvents(
    {
      patientId: note.patientId
    },
    isEnabled
  );
  for (const event of data.events) {
    const _meta = JSON.parse(event.meta);
    if (ALERT_RELATED_EVENT_TYPES.includes(event.type)) {
      if (
        _meta?.alertId === alertId ||
        (Array.isArray(_meta?.alertIds) && _meta.alertIds.includes(alertId))
      ) {
        related.push(event);
      }
    }
  }
  return related;
};

const AlertNote: React.FC<NoteProps> = ({ note }) => {
  const { terms } = useMetaData();
  const [isRelatedOpen, setIsRelatedOpen] = useState(false);
  const related = GetRelatedAlertEvents(note, true);
  const eventTypes = getEventTypes(terms);
  const meta = JSON.parse(note.meta);
  const config = eventTypes[EventTypes.Alert];
  return (
    <NoteFrame>
      <NoteIcon icon={config.icon} iconColor={config.iconColor} />
      <NoteMessage app={note.app} date={note.createdAt} message={meta.body} />
      <NoteAlertId alertId={meta.alertId} />
      <div className="w-full border-t-solid border-1 border-t-1 border-light-grey">
        {related.length > 0 ? (
          <Collapse
            isOpen={isRelatedOpen}
            setIsOpen={setIsRelatedOpen}
            header={
              <div>
                <Icon icon={faHistory} className="mr-2" />
                View Alert History
              </div>
            }
          >
            <div className="mb-4 px-8 pt-6 pb-1 bg-light-grey">
              {related.map((data, i) => {
                return <RelatedAlertNote key={i} note={data} />;
              })}
            </div>
          </Collapse>
        ) : null}
      </div>
    </NoteFrame>
  );
};

const RelatedAlertNote: React.FC<NoteProps> = ({ note }) => {
  const { terms } = useMetaData();
  const eventTypes = getEventTypes(terms);
  const config = eventTypes[note.type];
  const meta = JSON.parse(note.meta);
  let action = 'snooze';
  if (
    note.type === EventTypes.AlertResolved ||
    note.type === EventTypes.PatientResolved
  ) {
    action = 'resolve';
  }
  if (note.type === EventTypes.AlertActionNote) {
    action = '';
  }
  return (
    <NoteFrame>
      <NoteIcon icon={config.icon} iconColor={config.iconColor} />
      <NoteMessage app={note.app} date={note.createdAt} message={''}>
        {meta.reason && (
          <>
            <b>Reason: </b> {meta.reason}.
          </>
        )}
        {action === 'snooze' ? (
          ` - Snoozed until ${FormatTime(meta.snoozedUntil)}`
        ) : note.type === 'pr' ? (
          ` (via resolve ${terms.patient.default})`
        ) : note.type === 'an' ? (
          <NoteNote note={meta.note} />
        ) : null}
      </NoteMessage>
      <NoteOwner contactId={note.contactId} userId={note.userId} />
    </NoteFrame>
  );
};

const SnoozedAlertNote: React.FC<NoteProps> = ({ note }) => {
  const { terms } = useMetaData();
  const eventTypes = getEventTypes(terms);
  const config = eventTypes[note.type];
  const meta = JSON.parse(note.meta);
  return (
    <NoteFrame>
      <NoteIcon icon={config.icon} iconColor={config.iconColor} />
      <NoteMessage
        app={note.app}
        date={note.createdAt}
        message={config.description}
      >
        :&nbsp;
        <span className="text-gray1">#{meta.alertId}</span>
        .&nbsp;
        <b>Reason:</b> {meta.reason}
      </NoteMessage>
      <NoteOwner userId={note.userId} />
    </NoteFrame>
  );
};

const UnsnoozedAlertNote: React.FC<NoteProps> = ({ note }) => {
  const { terms } = useMetaData();
  const eventTypes = getEventTypes(terms);
  const config = eventTypes[note.type];
  const meta = JSON.parse(note.meta);
  return (
    <NoteFrame>
      <NoteIcon icon={config.icon} iconColor={config.iconColor} />
      <NoteMessage
        app={note.app}
        date={note.createdAt}
        message={config.description}
      >
        :&nbsp;
        <span className="text-gray1">#{meta.alertId}</span>.
      </NoteMessage>
      <NoteOwner userId={note.userId} />
    </NoteFrame>
  );
};

const ResolvedAlertNote: React.FC<NoteProps> = ({ note }) => {
  const { terms } = useMetaData();
  const eventTypes = getEventTypes(terms);
  const config = eventTypes[note.type];
  const meta = JSON.parse(note.meta);
  const misc = [];
  // TODO: update to use configs
  if (meta.misc) {
    for (const entry in meta.misc) {
      if (entry === 'ed_averted') {
        misc.push('ED Averted');
      } else if (entry === 'misentry') {
        misc.push(`${terms.patient.capitalized} Misentry`);
      }
    }
  }
  return (
    <NoteFrame>
      <NoteIcon icon={config.icon} iconColor={config.iconColor} />
      <NoteMessage
        app={note.app}
        date={note.createdAt}
        message={config.description}
      >
        :&nbsp;
        <span className="text-gray1">#{meta.alertId}</span>
        .&nbsp;
        <b>Reason:</b> {meta.reason}
        {misc.length > 0 ? ` | Additional: ${misc.join(', ')}` : null}
      </NoteMessage>
      <NoteOwner userId={note.userId} />
    </NoteFrame>
  );
};

const Note: React.FC<NoteProps> = ({ note }) => {
  const { terms } = useMetaData();
  const { isReadOnly } = useMetaData();
  const { user } = useAuth();
  const eventTypes = getEventTypes(terms);
  const config = eventTypes[note.type];
  const meta = JSON.parse(note.meta);
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(meta.note);
  const [currentNote, setCurrentNote] = useState(meta.note);
  const [isSaving, setIsSaving] = useState(false);

  const canEditOwner =
    !isReadOnly && note.type === EventTypes.Note && user.id === note.userId;

  const handleEdit = () => {
    setEditText(currentNote);
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!editText || editText.trim().length === 0) {
      Toast({
        intent: 'warning',
        message: 'Note cannot be empty!',
        timeout: 3000
      });
      return;
    }

    setIsSaving(true);
    try {
      const response = await EditEventNote({
        id: note.id,
        note: editText.trim()
      });

      if (response.status === 'OK') {
        // Update the current note to reflect the new content
        setCurrentNote(editText.trim());

        Toast({
          intent: 'success',
          message: 'Note updated successfully!',
          timeout: 3000
        });
        setIsEditing(false);
      } else {
        Toast({
          intent: 'danger',
          message: response.message || 'Failed to update note',
          timeout: 5000
        });
      }
    } catch {
      Toast({
        intent: 'danger',
        message: 'An error occurred while updating the note',
        timeout: 5000
      });
    }
    setIsSaving(false);
  };

  const handleCancel = () => {
    setEditText(currentNote);
    setIsEditing(false);
  };

  return (
    <NoteFrame>
      <NoteIcon icon={config.icon} iconColor={config.iconColor} />
      <NoteMessage
        app={note.app}
        date={note.createdAt}
        message={config.description}
      >
        {isEditing ? (
          <div className="bg-light-grey rounded pa-4 mt-4 mb-2">
            <strong>Note:</strong>
            <textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              className="w-full mt-2 pa-2 border rounded"
              rows={3}
              placeholder="Enter your note here..."
            />
            <div className="flex justify-end mt-2">
              <Button
                variant="minimal"
                className="mr-2"
                onClick={handleCancel}
                disabled={isSaving}
              >
                <Icon icon={faTimes} className="mr-1" />
                Cancel
              </Button>
              <Button
                intent="success"
                onClick={handleSave}
                loading={isSaving}
                disabled={isSaving}
              >
                <Icon icon={faSave} className="mr-1" />
                Save
              </Button>
            </div>
          </div>
        ) : (
          <NoteNote note={currentNote} />
        )}
      </NoteMessage>
      <NoteOwner
        contactId={note.contactId}
        userId={note.userId}
        note={note}
        onEdit={canEditOwner ? handleEdit : undefined}
      />
    </NoteFrame>
  );
};

const PrescriptionNote: React.FC<NoteProps> = ({ note }) => {
  const { terms } = useMetaData();
  const eventTypes = getEventTypes(terms);
  const config = eventTypes[note.type];
  return (
    <NoteFrame>
      <NoteIcon icon={config.icon} iconColor={config.iconColor} />
      <NoteMessage
        app={note.app}
        date={note.createdAt}
        message={config.description}
      />
      <NoteOwner contactId={note.contactId} userId={note.userId} />
    </NoteFrame>
  );
};

const PRESCRIPTION_LABELS: Record<string, string> = {
  end: 'End Date',
  method: 'Communication Method',
  language: 'Language',
  contact: 'Contact',
  medications: 'Medications',
  tz: 'Timezone'
};

const PRESCRIPTION_LANGUAGES: Record<string, string> = {
  en: 'English',
  es: 'Spanish'
};

const formatPrescriptionChangeValue = (field: string, val: any): string => {
  if (val === undefined || val === null) {
    return 'None';
  }
  if (field === 'end') {
    return FormatDate(new Date(val));
  }
  if (field === 'language') {
    return PRESCRIPTION_LANGUAGES[val] || val;
  }
  if (field === 'contact' && typeof val === 'object') {
    return val.displayName || val.name || 'Set';
  }
  if (field === 'days') {
    return String(val)
      .split(',')
      .map((d) => WEEKDAYS_SHORT[parseInt(d.trim(), 10)])
      .join(' ');
  }
  if (field === 'medications') {
    if (Array.isArray(val) && val.length > 0) {
      return val
        .map((m: any) => m.name || m.drugName || 'Medication')
        .join(', ');
    }
    if (typeof val === 'string') {
      try {
        const parsed = JSON.parse(val);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed
            .map((m: any) => m.name || m.drugName || 'Medication')
            .join(', ');
        }
      } catch (e) {
        Toast({
          intent: 'danger',
          message: 'Failed to parse medication data',
          timeout: 5000
        });
      }
      return 'None';
    }
  }
  return String(val);
};

const getMedicationObjects = (val: any): any[] => {
  if (Array.isArray(val)) {
    return val;
  }
  if (typeof val === 'string') {
    try {
      const parsed = JSON.parse(val);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    } catch (e) {
      Toast({
        intent: 'danger',
        message: 'Failed to parse medication data',
        timeout: 5000
      });
    }
  }
  return [];
};

const EditPrescriptionNote: React.FC<NoteProps> = ({ note }) => {
  const { terms } = useMetaData();
  const eventTypes = getEventTypes(terms);
  const config = eventTypes[note.type];
  const meta = JSON.parse(note.meta);
  let changesDisplay = null;
  if (meta.changes && meta.changes.length > 0) {
    changesDisplay = meta.changes
      .filter((c: any) => c.field !== '_fieldsChanged')
      .map((c: any) => {
        const label = PRESCRIPTION_LABELS[c.field] || c.field;
        if (c.field === 'medications') {
          const oldList = getMedicationObjects(c.old);
          const newList = getMedicationObjects(c.new);
          const parts: string[] = [];
          const added = newList.filter(
            (n) => !oldList.some((o) => o.uid === n.uid)
          );
          if (added.length > 0) {
            const names = added
              .map((m) => m.name || m.drugName || 'Medication')
              .join(', ');
            parts.push(`added ${names}`);
          }
          const removed = oldList.filter(
            (o) => !newList.some((n) => n.uid === o.uid)
          );
          if (removed.length > 0) {
            const names = removed
              .map((m) => m.name || m.drugName || 'Medication')
              .join(', ');
            parts.push(`removed ${names}`);
          }
          const common = newList.filter((n) =>
            oldList.some((o) => o.uid === n.uid)
          );
          common.forEach((newItem) => {
            const oldItem = oldList.find((o) => o.uid === newItem.uid);
            if (!oldItem) return;
            const medChanges: string[] = [];
            if (
              JSON.stringify(newItem.times) !== JSON.stringify(oldItem.times)
            ) {
              const oldTime = Array.isArray(oldItem.times)
                ? oldItem.times.join(', ')
                : oldItem.times;
              const newTime = Array.isArray(newItem.times)
                ? newItem.times.join(', ')
                : newItem.times;
              medChanges.push(`times: ${oldTime} -> ${newTime}`);
            }
            if (String(newItem.days) !== String(oldItem.days)) {
              medChanges.push(`days: ${oldItem.days} -> ${newItem.days}`);
            }
            if (newItem.start !== oldItem.start) {
              const oldStart = FormatDate(oldItem.start);
              const newStart = FormatDate(newItem.start);
              if (oldStart !== newStart) {
                medChanges.push(`start: ${oldStart} -> ${newStart}`);
              }
            }
            if (
              (newItem.name || newItem.drugName) !==
              (oldItem.name || oldItem.drugName)
            ) {
              medChanges.push(
                `name: ${oldItem.name || oldItem.drugName} -> ${newItem.name || newItem.drugName}`
              );
            }
            if (medChanges.length > 0) {
              parts.push(
                `updated ${newItem.name || newItem.drugName || 'Medication'} (${medChanges.join(', ')})`
              );
            }
          });
          if (parts.length > 0) {
            return `${label}: ${parts.join('; ')}`;
          }
        }
        const oldVal = formatPrescriptionChangeValue(c.field, c.old);
        const newVal = formatPrescriptionChangeValue(c.field, c.new);
        if (oldVal === newVal) {
          return null;
        }
        return `${label}: ${oldVal} -> ${newVal}`;
      })
      .filter((c: string | null) => c !== null)
      .join('; ');
  }
  return (
    <NoteFrame>
      <NoteIcon icon={config.icon} iconColor={config.iconColor} />
      <NoteMessage app={note.app} date={note.createdAt}>
        <span>
          Updated prescription
          {changesDisplay && `: ${changesDisplay}`}
        </span>
      </NoteMessage>
      <NoteOwner contactId={note.contactId} userId={note.userId} />
    </NoteFrame>
  );
};

const CancelPrescriptionNote: React.FC<NoteProps> = ({ note }) => {
  const { terms } = useMetaData();
  const eventTypes = getEventTypes(terms);
  const config = eventTypes[note.type];
  return (
    <NoteFrame>
      <NoteIcon icon={config.icon} iconColor={config.iconColor} />
      <NoteMessage
        app={note.app}
        date={note.createdAt}
        message={config.description}
      />
      <NoteOwner contactId={note.contactId} userId={note.userId} />
    </NoteFrame>
  );
};

const EDIT_TYPES = (terms: any): any => {
  return {
    phone: 'Phone number',
    caregiverPhone: 'Caregiver phone number',
    phoneIsCaregiver: `${terms.patient.capitalized} phone number is caregiver\'s`,
    firstName: 'First name',
    middleInital: 'Middle initial',
    lastName: 'Last name',
    dob: 'Date of birth',
    note: `${terms.patient.capitalized} description`,
    ccmEligible: 'Chronic Care Management (CCM) eligible',
    mrn: `(${terms.mrn.capitalized})`,
    tags: `${terms.patient.capitalized} tags`
  };
};

// Helper to render safe HTML that already went through backend sanitization
const HtmlContent: React.FC<{ content: string }> = ({ content }) => {
  const isHtml = /<[^>]+>/.test(content);
  if (isHtml) {
    const inlineContent = content
      .replace(/<\/?p[^>]*>/g, '')
      .replace(/<\/?div[^>]*>/g, '')
      .replace(/<br\s*\/?>/g, ' ');
    return <span dangerouslySetInnerHTML={{ __html: inlineContent }} />;
  }
  return <span>{content}</span>;
};
const EditNote: React.FC<NoteProps> = ({ note }) => {
  const { terms } = useMetaData();
  const eventTypes = getEventTypes(terms);
  const config = eventTypes[note.type];
  if (note.meta) {
    const meta = JSON.parse(note.meta);
    let messages = `${terms.patient.capitalized} edited.`;
    const fields = meta.fields;
    if (fields && fields.length) {
      const _messages = [];
      for (const field of fields) {
        const name = field.name;
        const from = field.fromValue;
        const to = field.toValue;
        let msg = EDIT_TYPES(terms)[name];
        if (from === to) {
          continue;
        } else if (
          name === 'ccmEligible' ||
          (name === 'phoneIsCaregiver' && to)
        ) {
          msg += ` set to ${to}.`;
          _messages.push(msg);
        } else {
          if (from && to) {
            msg += ` updated from "${from}" to "${to}".`;
          } else if (!to && from) {
            msg += ` "${from}" removed.`;
          } else if (!from && to) {
            msg += ` "${to}" added.`;
          }
          _messages.push(msg);
        }
      }
      messages = _messages.join(' ');
    }
    return (
      <NoteFrame>
        <NoteIcon icon={config.icon} iconColor={config.iconColor} />
        <NoteMessage app={note.app} date={note.createdAt}>
          <HtmlContent content={messages} />
        </NoteMessage>
        <NoteOwner contactId={note.contactId} userId={note.userId} />
      </NoteFrame>
    );
  } else {
    return (
      <NoteFrame>
        <NoteIcon icon={config.icon} iconColor={config.iconColor} />
        <NoteMessage
          app={note.app}
          date={note.createdAt}
          message={config.description}
        />
        <NoteOwner contactId={note.contactId} userId={note.userId} />
      </NoteFrame>
    );
  }
};

const SnoozePatientNote: React.FC<NoteProps> = ({ note }) => {
  const { terms } = useMetaData();
  const eventTypes = getEventTypes(terms);
  const config = eventTypes[note.type];
  let message = config.description;
  const meta = JSON.parse(note.meta);
  if (meta.alertIds?.length > 0) {
    const alertWord = meta.alertIds.length === 1 ? 'alert' : 'alerts';
    message += ` (and ${meta.alertIds.length} ${alertWord})`;
  }
  message += ` until ${FormatDateTime(meta.snoozedUntil)}. Reason: ${
    meta.reason
  }.`;
  if (meta.note) {
    message += ` Note: "${meta.note}"`;
  }
  return (
    <NoteFrame>
      <NoteIcon icon={config.icon} iconColor={config.iconColor} />
      <NoteMessage app={note.app} date={note.createdAt} message={message} />
      <NoteOwner contactId={note.contactId} userId={note.userId} />
    </NoteFrame>
  );
};

const UnsnoozedPatientNote: React.FC<NoteProps> = ({ note }) => {
  const { terms } = useMetaData();
  const eventTypes = getEventTypes(terms);
  const config = eventTypes[note.type];
  let message = config.description;
  const meta = JSON.parse(note.meta);
  if (meta.alertIds?.length > 0) {
    const alertWord = meta.alertIds.length === 1 ? 'alert' : 'alerts';
    message += ` (and ${meta.alertIds.length} ${alertWord})`;
  }
  return (
    <NoteFrame>
      <NoteIcon icon={config.icon} iconColor={config.iconColor} />
      <NoteMessage app={note.app} date={note.createdAt} message={message} />
      <NoteOwner contactId={note.contactId} userId={note.userId} />
    </NoteFrame>
  );
};

const ResolvePatientNote: React.FC<NoteProps> = ({ note }) => {
  const { terms } = useMetaData();
  const eventTypes = getEventTypes(terms);
  const config = eventTypes[note.type];
  const meta = JSON.parse(note.meta);
  return (
    <NoteFrame>
      <NoteIcon icon={config.icon} iconColor={config.iconColor} />
      <NoteMessage
        app={note.app}
        date={note.createdAt}
        message={`${config.description}. `}
      >
        {meta.reason && (
          <>
            <b>Reason: </b> {meta.reason}.&nbsp;
          </>
        )}
        {meta.alertIds && meta.alertIds.length
          ? 'Resolved all open alerts. '
          : null}
        {meta.customData?.avertedEd ? 'Averted ED Visit. ' : null}
        {meta.note ? <NoteNote note={meta.note} /> : null}
      </NoteMessage>
      <NoteOwner userId={note.userId} />
    </NoteFrame>
  );
};

const SuspendNote: React.FC<NoteProps> = ({ note }) => {
  const { terms } = useMetaData();
  const eventTypes = getEventTypes(terms);
  const config = eventTypes[note.type];
  let message = config.description;
  // Have to set type here instead of on the model to note conflict with other event type metas
  type SuspensionMeta = { startDate: string; endDate: string };

  if (note.type === 'pp' && note.meta) {
    let metaObj: SuspensionMeta;
    try {
      metaObj =
        typeof note.meta === 'string'
          ? (JSON.parse(note.meta) as SuspensionMeta)
          : (note.meta as SuspensionMeta);
    } catch {
      metaObj = { startDate: '', endDate: '' };
    }
    const startDate = new Date(metaObj.startDate).toLocaleDateString();
    const endDate = new Date(metaObj.endDate).toLocaleDateString();
    message = `Patient paused from the start of ${startDate} to the start of ${endDate}`;
  }

  if (note.type === 'ppu' && note.meta) {
    let metaObj: SuspensionMeta;
    try {
      metaObj =
        typeof note.meta === 'string'
          ? (JSON.parse(note.meta) as SuspensionMeta)
          : (note.meta as SuspensionMeta);
    } catch {
      metaObj = { startDate: '', endDate: '' };
    }
    const startDate = new Date(metaObj.startDate).toLocaleDateString();
    const endDate = new Date(metaObj.endDate).toLocaleDateString();
    message = `Cancelled paused from ${startDate} to ${endDate}`;
  }

  return (
    <NoteFrame>
      <NoteIcon icon={config.icon} iconColor={config.iconColor} />
      <NoteMessage app={note.app} date={note.createdAt} message={message} />
      <NoteOwner userId={note.userId} />
    </NoteFrame>
  );
};

const getArchiveReason = (settings: IAccountBaseSettings, meta: any) => {
  if (!settings) {
    return;
  }
  if (!settings.archiveOptions) {
    return;
  }
  if (!settings.archiveOptions.length) {
    return;
  }
  if (!meta) {
    return;
  }
  if (!meta.reason) {
    return;
  }
  const option = settings.archiveOptions.find(
    (option) => option.value === meta.reason
  );
  if (!option) {
    return;
  }
  return option.label;
};

const ArchiveNote: React.FC<NoteProps> = ({ note }) => {
  const { terms } = useMetaData();
  const { settings } = useMetaData();
  const eventTypes = getEventTypes(terms);
  const config = eventTypes[note.type];
  const meta = note.meta ? JSON.parse(note.meta) : {};
  const archiveReason = getArchiveReason(settings, meta);
  return (
    <NoteFrame>
      <NoteIcon icon={config.icon} iconColor={config.iconColor} />
      <NoteMessage
        app={note.app}
        date={note.createdAt}
        message={config.description}
      >
        {archiveReason ? (
          <React.Fragment>
            .&nbsp;
            <b>Reason:</b> {archiveReason}
          </React.Fragment>
        ) : null}
        {meta.note ? <NoteNote note={meta.note} /> : null}
      </NoteMessage>
      <NoteOwner userId={note.userId} />
    </NoteFrame>
  );
};

const InvalidateNote: React.FC<NoteProps> = ({ note }) => {
  const { terms } = useMetaData();
  const eventTypes = getEventTypes(terms);
  const config = eventTypes[note.type];
  const meta = JSON.parse(note.meta);
  const fieldDisplay = getInvalidationFieldDisplay(note.app, meta.key);

  return (
    <NoteFrame>
      <NoteIcon icon={config.icon} iconColor={config.iconColor} />
      <NoteMessage
        app={note.app}
        date={note.createdAt}
        message={config.description}
      >
        <>
          &nbsp;<b>Field: </b>
          <span>{fieldDisplay}</span>
        </>
        {meta.previousValue !== undefined && (
          <>
            &nbsp;<b>Previous Value: </b>
            <span>{getInvalidationValueDisplay(meta.previousValue)}</span>
          </>
        )}
        {meta.newValue !== undefined && (
          <>
            &nbsp;<b>New Value: </b>
            <span>{getInvalidationValueDisplay(meta.newValue)}</span>
          </>
        )}
        {meta.note && <NoteNote note={meta.note} />}
      </NoteMessage>
      <NoteOwner userId={note.userId} />
    </NoteFrame>
  );
};

const NewOrderNote: React.FC<NoteProps> = ({ note }) => {
  const { terms } = useMetaData();
  const eventTypes = getEventTypes(terms);
  const { icon, iconColor } = eventTypes[note.type];
  return (
    <NoteFrame>
      <NoteIcon icon={icon} iconColor={iconColor} />
      <NoteMessage
        app={note.app}
        date={note.createdAt}
        message={`EMR order for ${note.app}.`}
      />
      <NoteOwner contactId={note.contactId} userId={note.userId} />
    </NoteFrame>
  );
};

const PushMessageNote: React.FC<NoteProps> = ({ note }) => {
  const { terms } = useMetaData();
  const eventTypes = getEventTypes(terms);
  const { icon, iconColor } = eventTypes[note.type];
  const body: string[] = [];
  if (note._messages) {
    for (const message of note._messages) {
      body.push(message.body);
    }
  }
  return (
    <NoteFrame>
      <NoteIcon icon={icon} iconColor={iconColor} />
      <NoteMessage app={note.app} date={note.createdAt} message="Push Message ">
        <b>Content: </b>
        {body.join(' | ') || ''}
      </NoteMessage>
      <NoteOwner contactId={note.contactId} userId={note.userId} />
    </NoteFrame>
  );
};

const formattedDuration = (duration: number) => {
  const minutes = Math.floor(duration / 60);
  const seconds = duration % 60;
  const formattedSeconds = seconds < 10 ? `0${seconds}` : `${seconds}`;
  return {
    minutes: minutes.toString(),
    seconds: formattedSeconds
  };
};

const TimeTrackedNote = ({ note }: NoteProps): JSX.Element => {
  const { terms } = useMetaData();
  const eventTypes = getEventTypes(terms);
  const config = eventTypes[note.type];
  const meta = JSON.parse(note.meta);
  const duration = formattedDuration(meta.durationInSeconds);
  const minuteWord = duration.minutes === '1' ? 'minute' : 'minutes';
  const secondWord = duration.seconds === '01' ? 'second' : 'seconds';
  return (
    <NoteFrame>
      <NoteIcon icon={config.icon} iconColor={config.iconColor} />
      <NoteMessage
        app={note.app}
        date={note.createdAt}
        message={config.description}
      >
        <>
          :{' '}
          <span>
            {duration.minutes} {minuteWord} and {duration.seconds} {secondWord}{' '}
            with {terms.patient.default}
          </span>
        </>
        {meta.note && meta.note.length ? (
          <div className="mt-2 pa-3 bg-light-grey">{meta.note}</div>
        ) : null}
      </NoteMessage>
      <NoteOwner userId={note.userId} />
    </NoteFrame>
  );
};

export default PatientEventNote;
